'use client';

import { useCallback, useMemo } from 'react';
import { defaultBasketData } from 'constant';
import {
  useAddPromoCodeMutation,
  useGetShoppingCartQuery
} from 'services/shoppingCartApi';

import { Box, useTheme } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { formatCurrency, getAvailableShipment, sortShopList } from 'utils';
import { getCartData } from 'utils/cartMapping';

import { ShopCartItem } from 'types';

import { ContactUsErrorModal } from 'components/ErrorModals';
import { Divider } from 'ui';
import { ContentLoader } from '../../../ContentLoader';
import { PromoCode } from '../../../PromoCode';
import { ShopCartFooter } from '../ShopCartFooter';
import { ShopCartHeader } from '../ShopCartHeader';
import { ShopItem } from '../ShopItem';

type ShopCartListProps = {
  isAuthenticated: boolean;
  showMixedFulfillmentTag: boolean;
};

const ShopCartList = ({
  isAuthenticated,
  showMixedFulfillmentTag
}: ShopCartListProps) => {
  const {
    colors: {
      palette: {
        shop: {
          cart: { detailsStep: styles }
        }
      }
    }
  } = useTheme();

  const [addPromoCode, { isError, error: errorText, reset, isLoading }] =
    useAddPromoCodeMutation();
  const { guest } = useAppSelector((state) => state.guest);
  const { selectedPackages, selectedProducts } = useAppSelector(
    (state) => state.cart
  );
  const { data: shopData = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isAuthenticated
  });

  const localCartData = useMemo(
    () =>
      getCartData(
        isAuthenticated,
        shopData,
        selectedProducts,
        selectedPackages
      ),
    [isAuthenticated, shopData, selectedProducts, selectedPackages]
  );
  const cartData = isAuthenticated ? shopData : localCartData;

  const isShipmentExist = useMemo(
    () => cartData.items.some(getAvailableShipment),
    [cartData]
  );

  const sortedData: ShopCartItem[] = useMemo(
    () => sortShopList(cartData.items),
    [cartData]
  );

  const invoiceId = guest?.invoice_id || cartData.details?.invoice_id || 0;

  const handleSetDiscount = async (promoCodeValue: string) =>
    addPromoCode({
      invoice_id: invoiceId,
      promo_code: promoCodeValue
    });

  const getShippingCost = () => {
    if (
      cartData.details?.shipping_id !== null &&
      cartData.details?.shipping_cost
    ) {
      return formatCurrency(cartData.details?.shipping_cost);
    }
    if (
      cartData.details?.shipping_id !== null &&
      cartData.details?.shipping_cost === 0
    ) {
      return 'Free';
    }
    return null;
  };

  const discounts = useMemo(
    () => (cartData.items ?? []).filter((item) => item.is_promo),
    [cartData]
  );

  const getDiscountPrice = useCallback(
    (itemId: number) =>
      discounts
        .filter((item) => item?.rel_cart_item_id === itemId)
        .reduce((a, b) => a + (b?.amount_total ?? 0), 0),
    [discounts]
  );

  return sortedData.length > 0 ? (
    <Box>
      <Divider bg={styles.divider} height='2px' border='none' />
      <ShopCartHeader />

      {sortedData.map((item) => (
        <ShopItem
          key={item.cart_item_id}
          item={item}
          invoiceId={invoiceId}
          discount={getDiscountPrice(item.cart_item_id)}
          isAuthenticated={isAuthenticated}
          isShowTag={showMixedFulfillmentTag}
        />
      ))}
      <Divider bg={styles.divider} height='1px' border='none' />
      {isAuthenticated && (
        <PromoCode
          my='15px'
          onSaveDiscount={handleSetDiscount}
          isError={isError}
          onClearError={reset}
          gap='30px'
          variant='dark'
        />
      )}
      <Divider bg={styles.divider} height='1px' border='none' />

      <ShopCartFooter
        subTotal={formatCurrency(cartData.details?.total_items_cost ?? 0)}
        tax={formatCurrency(cartData.details?.tax_cost ?? 0)}
        shippingCost={isAuthenticated ? getShippingCost() : null}
        total={formatCurrency(cartData.details?.total_cost ?? 0)}
        gap={0}
        isShipmentExist={isAuthenticated && isShipmentExist}
        isShowTaxes={isAuthenticated}
      />
      <ContactUsErrorModal
        errorText={errorText as string}
        onClearHandler={reset}
      />
    </Box>
  ) : (
    <Box>
      <Divider bg={styles.divider} height='1px' border='none' />
      <ContentLoader
        isLoading={isLoading}
        label='Cart is Empty'
        minHeight='300px'
      />
      <Divider bg={styles.divider} height='1px' border='none' />
    </Box>
  );
};

export default ShopCartList;
